import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const ICON_OPTIONS = [
  { value: 'leaf', label: 'Leaf' },
  { value: 'brain', label: 'Brain' },
  { value: 'heart', label: 'Heart' },
  { value: 'users', label: 'Users' },
  { value: 'star', label: 'Star' },
  { value: 'lightbulb', label: 'Lightbulb' },
  { value: 'shield', label: 'Shield' },
  { value: 'hands-helping', label: 'Hands Helping' },
  { value: 'smile', label: 'Smile' },
  { value: 'graduation-cap', label: 'Graduation Cap' },
  { value: 'book', label: 'Book' },
  { value: 'trophy', label: 'Trophy' },
  { value: 'handshake', label: 'Handshake' },
  { value: 'comments', label: 'Comments' },
  { value: 'user-friends', label: 'User Friends' },
  { value: 'home', label: 'Home' }
];

function StudentLifeCharacterShapingForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    zones: {
      easternCulture: {
        title: '',
        subtitle: '',
        icon: 'leaf',
        pillars: []
      },
      sel: {
        title: '',
        subtitle: '',
        icon: 'brain',
        pillars: []
      },
      wellbeing: {
        title: '',
        subtitle: '',
        icon: 'heart',
        pillars: []
      }
    }
  });

  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBase}/studentlife/cms/sections?section_type=student-life-character-shaping`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('[CharacterShapingForm] API response:', result);

      if (result.ok && result.data && result.data.length > 0) {
        const section = result.data[0];
        const content = section.content_draft?.en || section.content_published?.en || {};

        const loadedData = {
          kicker: content.kicker || '',
          title: content.title || '',
          description: content.description || '',
          zones: {
            easternCulture: {
              title: content.zones?.easternCulture?.title || '',
              subtitle: content.zones?.easternCulture?.subtitle || '',
              icon: content.zones?.easternCulture?.icon || 'leaf',
              pillars: content.zones?.easternCulture?.pillars || []
            },
            sel: {
              title: content.zones?.sel?.title || '',
              subtitle: content.zones?.sel?.subtitle || '',
              icon: content.zones?.sel?.icon || 'brain',
              pillars: content.zones?.sel?.pillars || []
            },
            wellbeing: {
              title: content.zones?.wellbeing?.title || '',
              subtitle: content.zones?.wellbeing?.subtitle || '',
              icon: content.zones?.wellbeing?.icon || 'heart',
              pillars: content.zones?.wellbeing?.pillars || []
            }
          }
        };

        setFormData(loadedData);
        setOriginalData(JSON.parse(JSON.stringify(loadedData)));
      }
    } catch (error) {
      console.error('[CharacterShapingForm] Fetch error:', error);
      toast.error('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const hasFieldChanged = () => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  useEffect(() => {
    setHasChanges(hasFieldChanged());
  }, [formData, originalData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleZoneChange = (zoneName, field, value) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneName]: {
          ...prev.zones[zoneName],
          [field]: value
        }
      }
    }));
  };

  const handlePillarChange = (zoneName, pillarIndex, field, value) => {
    setFormData(prev => {
      const newZones = { ...prev.zones };
      const newPillars = [...newZones[zoneName].pillars];
      newPillars[pillarIndex] = {
        ...newPillars[pillarIndex],
        [field]: value
      };
      newZones[zoneName] = {
        ...newZones[zoneName],
        pillars: newPillars
      };
      return { ...prev, zones: newZones };
    });
  };

  const handlePointChange = (zoneName, pillarIndex, pointIndex, value) => {
    setFormData(prev => {
      const newZones = { ...prev.zones };
      const newPillars = [...newZones[zoneName].pillars];
      const newPoints = [...newPillars[pillarIndex].points];
      newPoints[pointIndex] = value;
      newPillars[pillarIndex] = {
        ...newPillars[pillarIndex],
        points: newPoints
      };
      newZones[zoneName] = {
        ...newZones[zoneName],
        pillars: newPillars
      };
      return { ...prev, zones: newZones };
    });
  };

  const addPillar = (zoneName) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneName]: {
          ...prev.zones[zoneName],
          pillars: [
            ...prev.zones[zoneName].pillars,
            { heading: '', points: [''] }
          ]
        }
      }
    }));
  };

  const removePillar = (zoneName, pillarIndex) => {
    setFormData(prev => {
      const newZones = { ...prev.zones };
      const newPillars = [...newZones[zoneName].pillars];
      newPillars.splice(pillarIndex, 1);
      newZones[zoneName] = {
        ...newZones[zoneName],
        pillars: newPillars
      };
      return { ...prev, zones: newZones };
    });
  };

  const addPoint = (zoneName, pillarIndex) => {
    setFormData(prev => {
      const newZones = { ...prev.zones };
      const newPillars = [...newZones[zoneName].pillars];
      newPillars[pillarIndex] = {
        ...newPillars[pillarIndex],
        points: [...newPillars[pillarIndex].points, '']
      };
      newZones[zoneName] = {
        ...newZones[zoneName],
        pillars: newPillars
      };
      return { ...prev, zones: newZones };
    });
  };

  const removePoint = (zoneName, pillarIndex, pointIndex) => {
    setFormData(prev => {
      const newZones = { ...prev.zones };
      const newPillars = [...newZones[zoneName].pillars];
      const newPoints = [...newPillars[pillarIndex].points];
      newPoints.splice(pointIndex, 1);
      newPillars[pillarIndex] = {
        ...newPillars[pillarIndex],
        points: newPoints
      };
      newZones[zoneName] = {
        ...newZones[zoneName],
        pillars: newPillars
      };
      return { ...prev, zones: newZones };
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const payload = {
        section_type: 'student-life-character-shaping',
        content: {
          en: {
            kicker: formData.kicker,
            title: formData.title,
            description: formData.description,
            zones: formData.zones
          }
        }
      };

      console.log('[CharacterShapingForm] Submitting:', payload);

      const response = await fetch(`${apiBase}/studentlife/cms/sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('[CharacterShapingForm] Submit response:', result);

      if (result.ok) {
        toast.success('✓ Changes saved as draft');
        await fetchData();
      } else {
        throw new Error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('[CharacterShapingForm] Submit error:', error);
      toast.error('Save failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);

      const response = await fetch(`${apiBase}/studentlife/cms/sections/submit-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_type: 'student-life-character-shaping'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (result.ok) {
        toast.success('✓ Submitted for approval');
        await fetchData();
      } else {
        throw new Error(result.error || 'Submit failed');
      }
    } catch (error) {
      console.error('[CharacterShapingForm] Submit approval error:', error);
      toast.error('Submit failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderZone = (zoneName, zoneData, zoneIcon, zoneTitle) => (
    <div className="form-field-card" key={zoneName}>
      <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <i className={`fas fa-${zoneIcon}`}></i> {zoneTitle}
      </h3>

      <div style={{ marginBottom: '16px' }}>
        <label className="form-label">Zone Title</label>
        <input
          type="text"
          className="form-input"
          value={zoneData.title}
          onChange={(e) => handleZoneChange(zoneName, 'title', e.target.value)}
          placeholder="Zone title"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label className="form-label">Zone Subtitle</label>
        <input
          type="text"
          className="form-input"
          value={zoneData.subtitle}
          onChange={(e) => handleZoneChange(zoneName, 'subtitle', e.target.value)}
          placeholder="Zone subtitle"
        />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label className="form-label">Icon</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            className="form-select"
            value={zoneData.icon}
            onChange={(e) => handleZoneChange(zoneName, 'icon', e.target.value)}
            style={{ flex: 1 }}
          >
            {ICON_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <i className={`fas fa-${zoneData.icon}`} style={{ fontSize: '20px', color: '#666' }}></i>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label className="form-label" style={{ marginBottom: '12px', display: 'block' }}>Pillars</label>
        {zoneData.pillars.map((pillar, pIdx) => (
          <div key={pIdx} className="nested-item-card" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong style={{ fontSize: '14px' }}>Pillar {pIdx + 1}</strong>
              <button
                type="button"
                className="btn-delete"
                onClick={() => removePillar(zoneName, pIdx)}
                style={{ padding: '4px 12px', fontSize: '13px' }}
              >
                <i className="fas fa-trash"></i> Remove
              </button>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="form-label">Heading</label>
              <input
                type="text"
                className="form-input"
                value={pillar.heading}
                onChange={(e) => handlePillarChange(zoneName, pIdx, 'heading', e.target.value)}
                placeholder="Pillar heading"
              />
            </div>

            <div>
              <label className="form-label">Points</label>
              {pillar.points.map((point, ptIdx) => (
                <div key={ptIdx} className="input-group-row" style={{ marginBottom: '8px' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={point}
                    onChange={(e) => handlePointChange(zoneName, pIdx, ptIdx, e.target.value)}
                    placeholder={`Point ${ptIdx + 1}`}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-icon-secondary"
                    onClick={() => removePoint(zoneName, pIdx, ptIdx)}
                    disabled={pillar.points.length === 1}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => addPoint(zoneName, pIdx)}
                style={{ marginTop: '8px', fontSize: '13px' }}
              >
                <i className="fas fa-plus"></i> Add Point
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn-primary"
          onClick={() => addPillar(zoneName)}
          style={{ marginTop: '8px' }}
        >
          <i className="fas fa-plus"></i> Add Pillar
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="form-container">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i> Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <ToastContainer />
      
      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">
            <i className="fas fa-tree"></i> Character Shaping Program
          </h2>
          <p className="hero-form-description">
            Manage the three interconnected pillars of character development
          </p>
        </div>

        {/* KICKER */}
        <div className="form-field-card">
          <label className="form-label">Kicker Text</label>
          <input
            type="text"
            className="form-input"
            value={formData.kicker}
            onChange={(e) => handleChange('kicker', e.target.value)}
            placeholder="e.g., Our Framework"
          />
        </div>

        {/* TITLE */}
        <div className="form-field-card">
          <label className="form-label">Title</label>
          <input
            type="text"
            className="form-input"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="e.g., Character Shaping Program"
          />
        </div>

        {/* DESCRIPTION */}
        <div className="form-field-card">
          <label className="form-label">Description</label>
          <textarea
            className="form-textarea"
            rows="3"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of the character shaping program"
          />
        </div>

        {/* ZONES */}
        {renderZone('easternCulture', formData.zones.easternCulture, 'leaf', 'Eastern Culture Zone')}
        {renderZone('sel', formData.zones.sel, 'brain', 'Social Emotional Learning (SEL) Zone')}
        {renderZone('wellbeing', formData.zones.wellbeing, 'heart', 'Wellbeing Zone')}

        {/* ACTION BUTTONS */}
        <div className="form-actions-fixed">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !hasChanges}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Draft
              </>
            )}
          </button>

          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmitForApproval}
            disabled={submitting || !hasChanges}
          >
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i> Submit for Approval
              </>
            )}
          </button>
        </div>

        {!hasChanges && (
          <p className="form-tip" style={{ marginTop: '16px', textAlign: 'center' }}>
            <i className="fas fa-info-circle"></i> Make changes to enable Save and Submit buttons
          </p>
        )}
      </div>
    </div>
  );
}

export default StudentLifeCharacterShapingForm;
